// The blocks aidev writes into a session's messages (a wait over, a child session's report, a ticket comment, the
// operator's reply relayed…): found in a message's text so that the page shows them for what they are, and shows the
// message as aidev's rather than the operator's. Only these tags, opening a line as aidev writes them: anything else
// stays text, nothing is hidden. A message has no author field, so one the operator typed with such a block at a
// line's start would read as aidev's too: rare, and its words are all shown.

export type InjectedSegment =
  | { kind: 'text'; text: string }
  | { kind: 'block'; tag: string; attrs: Record<string, string>; body: string };

/** What each block is, in the operator's words; `background` ones are context the session reads, folded at first */
export const KNOWN: Record<string, { label: string; background?: true }> = {
  'wait-result': { label: 'Wait over' },
  'session-report': { label: 'Report from a session' },
  'ticket-comment': { label: 'Comment on the ticket' },
  'operator-reply': { label: 'Your reply' },
  'operator-note': { label: 'Your note' },
  'approved-approach': { label: 'The approach you approved' },
  'pr-feedback': { label: 'Feedback on the pull request' },
  'ci-output': { label: 'CI output', background: true },
  'ticket-plans': { label: 'Plans of related tickets', background: true },
  'actor-profiles': { label: 'Profiles of the people involved', background: true },
  'writing-voice': { label: 'Your writing voice', background: true },
};

const TAGS = Object.keys(KNOWN).join('|');
const BLOCK = new RegExp(`(?<=^|\\n)<(${TAGS})((?:\\s+[a-z][\\w-]*="[^"]*")*)\\s*>\\n?([\\s\\S]*?)\\n?</\\1>`, 'g');
const ATTR = /([a-z][\w-]*)="([^"]*)"/g;

export function splitInjected(text: string): InjectedSegment[] {
  const out: InjectedSegment[] = [];
  let at = 0;
  for (const m of text.matchAll(BLOCK)) {
    const before = text.slice(at, m.index).trim();
    if (before) out.push({ kind: 'text', text: before });
    const attrs: Record<string, string> = {};
    for (const a of (m[2] ?? '').matchAll(ATTR)) attrs[a[1]!] = a[2]!;
    out.push({ kind: 'block', tag: m[1]!, attrs, body: m[3] ?? '' });
    at = m.index! + m[0].length;
  }
  const rest = text.slice(at).trim();
  if (rest) out.push({ kind: 'text', text: rest });
  return out;
}

/** The block's one line: what happened, with what its attributes say */
export function summary(seg: Extract<InjectedSegment, { kind: 'block' }>): string {
  const { tag, attrs, body } = seg;
  if (tag === 'wait-result') {
    const outcome = body.split('\n')[0]?.trim();
    const where = attrs.repo ? `Checks on ${attrs.repo}${attrs.sha ? ` at ${attrs.sha.slice(0, 7)}` : ''}` : 'Wait over';
    return outcome ? `${where}: ${outcome}` : `${where}: ${attrs.status ?? 'over'}`;
  }
  if (tag === 'session-report') {
    return [['Report from the session', attrs.name && `« ${attrs.name} »`].filter(Boolean).join(' '), attrs.status].filter(Boolean).join(', ');
  }
  return KNOWN[tag]!.label;
}

/** What the block holds below its line: a wait's first line is already in it */
export function details(seg: Extract<InjectedSegment, { kind: 'block' }>): string {
  if (seg.tag === 'wait-result') return seg.body.split('\n').slice(1).join('\n').trim();
  return seg.body.trim();
}
